import React, { useContext, useMemo } from "react";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";

import { parseStrapiImage } from "utils";
import LockedContentOverlay from "components/LockedContentOverlay";
import { CoreContext } from "context/CoreContext";
import { openLockedContentModal } from "utils/lockedContent";
import AudienceAccessIndicators from "components/AudienceAccessIndicators";
import { getContentAudiences } from "utils/audienceAccess";
import {
  ActionLabel,
  DocumentBadge,
  DocumentIcon,
  DocumentPreview,
  DocumentShell,
  DocumentTitle,
  MetaLine,
  PreviewFallback,
  PreviewImage,
  Root,
  TopMeta,
} from "./styled";

const getFileUrl = (item) => {
  const rawFile = item?.file || item?.document || item?.documentUrl;

  if (typeof rawFile === "string") return rawFile;
  if (rawFile?.url) return parseStrapiImage(rawFile.url);

  return null;
};

const getExtension = (url, item) => {
  const mime = `${item?.file?.mime || item?.mime || ""}`.toLowerCase();
  if (mime === "application/pdf") return "PDF";

  const cleanUrl = `${url || ""}`.split("?")[0].split("#")[0];
  const extension = cleanUrl.split(".").pop();
  return extension ? extension.toUpperCase() : "DOC";
};

export default function TrailDocumentCard({ item, handleDownload }) {
  const { setModal } = useContext(CoreContext);
  const locked = item?.accessState === "locked";
  const currentFile = useMemo(() => {
    const url = getFileUrl(item);
    const cleanUrl = `${url || ""}`.split("?")[0].split("#")[0];
    const extension = cleanUrl.split(".").pop();

    return {
      url,
      extension,
    };
  }, [item]);

  const previewImage = item?.image?.url
    ? parseStrapiImage(item.image.url)
    : item?.cover || item?.imageUrl;
  const extensionLabel = getExtension(currentFile.url, item);
  const metaLabels = Array.from(new Set([
    item?.category,
    item?.document_type,
    "Document",
  ].filter(Boolean)));

  return (
    <Root
      onClick={() => locked ? openLockedContentModal(setModal, item) : handleDownload(item, currentFile)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          locked ? openLockedContentModal(setModal, item) : handleDownload(item, currentFile);
        }
      }}
      aria-label={locked ? `${item?.title || "Document"}. Exclusive content` : `Open ${item?.title || "document"}`}
      aria-disabled={locked}
    >
      <DocumentShell>
        <TopMeta>
          <DocumentBadge>{extensionLabel}</DocumentBadge>
          <ActionLabel>
            {locked ? "Exclusive content" : "Preview"}
            {locked ? null : <OpenInNewOutlined fontSize="inherit" />}
          </ActionLabel>
        </TopMeta>

        <DocumentPreview>
          {previewImage ? (
            <PreviewImage src={previewImage} alt={item?.title || "Document preview"} draggable={false} />
          ) : (
            <PreviewFallback>
              <DocumentIcon>
                <DescriptionOutlined fontSize="large" />
              </DocumentIcon>
            </PreviewFallback>
          )}
        </DocumentPreview>
        <AudienceAccessIndicators audiences={getContentAudiences(item)} belowTopMeta />
        {locked ? <LockedContentOverlay /> : null}
      </DocumentShell>

      <DocumentTitle>{item?.title}</DocumentTitle>
      <MetaLine>
        {metaLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </MetaLine>
    </Root>
  );
}
